import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { Alert, Button, Card, Field, Input, PageHeader } from "@empite/ui-components";
import { useAcceptInvitation, useAdminSignIn } from "../admin-hooks";
import { ErrorAlert } from "./guards";

export interface AcceptInviteSearch {
  /** The raw invitation token, out of the link the inviter passed on. */
  token?: string;
}

/**
 * Accepting a seat in somebody else's tenant.
 *
 * The TOKEN is the whole authority. This screen asks for no email and offers
 * no tenant picker, because both come off the invitation row the server
 * resolves the token to — a form that asked which company to join would be
 * offering a choice the token has already made, and a mismatch between the two
 * would have to be resolved somewhere.
 *
 * The person here has no session, so this route sits outside RequireOperator,
 * exactly as the Admin API mounts POST /invites/accept outside its guarded
 * subtree. Accepting returns an operator and NO session; the sign-in that
 * follows is the ordinary one.
 */
const schema = z.object({
  token: z.string().min(1, "Paste the invitation token from your invite link."),
  display_name: z.string().max(120, "A name must be at most 120 characters."),
  password: z.string().min(1, "Choose a password."),
});

type Values = z.infer<typeof schema>;

export function AcceptInvite({ search }: { search: AcceptInviteSearch }) {
  const navigate = useNavigate();
  const accept = useAcceptInvitation();
  const signIn = useAdminSignIn();
  const [formError, setFormError] = useState<unknown>(null);
  // The account exists but the sign-in after it did not. Distinct from a
  // failed acceptance: the token is spent either way, so telling somebody to
  // "try again" here would send them back to a link that is now dead.
  const [accepted, setAccepted] = useState<string | null>(null);
  const form = useZodForm<Values>(schema, {
    defaultValues: { token: search.token ?? "", display_name: "", password: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    const displayName = values.display_name.trim();
    let email: string;
    try {
      const result = await accept.mutateAsync({
        token: values.token.trim(),
        display_name: displayName === "" ? undefined : displayName,
        password: values.password,
      });
      // The address off the invitation, not off this form: the invitation is
      // what says who this seat was for.
      email = result.operator.email;
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["token", "password"])) {
        setFormError(err);
      }
      return;
    }
    try {
      await signIn.mutateAsync({ email, password: values.password });
    } catch {
      setAccepted(email);
      return;
    }
    await navigate({ to: "/" });
  });

  if (accepted) {
    return (
      <>
        <PageHeader title="You are in" />
        <Card>
          <Alert tone="warning" title="Accepted, but not signed in">
            The account for <strong>{accepted}</strong> exists. Signing in straight afterwards did
            not work — use{" "}
            <Link to="/sign-in" search={{ next: undefined }}>the sign-in page</Link>. Your
            invitation link has been used and will not work a second time.
          </Alert>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Accept your invitation"
        description="Choose a password and you are an operator in the company that invited you — with the role they gave you, and nothing outside their tenant."
      />
      <Card>
        <form onSubmit={submit} noValidate>
          <ErrorAlert error={formError} title="That invitation was not accepted" />
          {search.token ? null : (
            <Alert tone="info" title="No token in this link">
              Open the link from your invitation, or paste its token below. An invitation expires,
              and the company that sent it can withdraw it.
            </Alert>
          )}
          <Field
            label="Invitation token"
            htmlFor="token"
            hint="Filled in from your invite link. Leave it as it is."
            error={form.formState.errors.token?.message}
          >
            <Input
              id="token"
              invalid={Boolean(form.formState.errors.token)}
              {...form.register("token")}
            />
          </Field>
          <Field
            label="Your name"
            htmlFor="display_name"
            hint="Optional. What your colleagues see in the console."
            error={form.formState.errors.display_name?.message}
          >
            <Input
              id="display_name"
              autoComplete="name"
              invalid={Boolean(form.formState.errors.display_name)}
              {...form.register("display_name")}
            />
          </Field>
          <Field
            label="Choose a password"
            htmlFor="password"
            hint="Only you ever know it — the person who invited you sets no password on your behalf."
            error={form.formState.errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              invalid={Boolean(form.formState.errors.password)}
              {...form.register("password")}
            />
          </Field>
          <Button type="submit" busy={accept.isPending || signIn.isPending}>
            Accept and sign in
          </Button>
        </form>
      </Card>
    </>
  );
}
